import { lazy } from "react";

/**
 * French Routes Configuration - COMPREHENSIVE
 * Complete routing setup for all French calculator components and category pages
 * Uses lazy loading for performance optimization
 * Total: 10 category pages + 276 individual calculator routes
 */

// ============================================================================
// CATEGORY LIST PAGES - French
// ============================================================================

// Health Calculators List
const HealthCalculatorsList = lazy(() => import("../Components/Fr/HealthCalculator/HealthCalculatorsList"));

// Physics Calculators List
const PhysicsCalculatorsList = lazy(() => import("../Components/Fr/PhysicsCalculator/PhysicsCalculatorsList"));

// Chemistry Calculators List
const ChemistryCalculatorsList = lazy(() => import("../Components/Fr/ChemistryCalculator/ChemistryCalculatorsList"));

// Math Calculators List
const MathCalculatorsList = lazy(() => import("../Components/Fr/MathCalculator/MathCalculatorsList"));

// Women Calculators List
const WomenCalculatorsList = lazy(() => import("../Components/Fr/WomenCalculator/WomenCalculatorsList"));

// Financial Calculators List
const FinancialCalculatorsList = lazy(() => import("../Components/Fr/FinancialCalculator/FinancialCalculatorsList"));

// Study Calculators List
const StudyCalculatorsList = lazy(() => import("../Components/Fr/StudyCalculator/StudyCalculatorsList"));

// Food Calculators List
const FoodCalculatorsList = lazy(() => import("../Components/Fr/FoodCalculator/FoodCalculatorsList"));

// Other Calculators List
const OtherCalculatorsList = lazy(() => import("../Components/Fr/OtherCalculator/OtherCalculatorsList"));

// Human/Date Calculators List
const HumanCalculatorsList = lazy(() => import("../Components/Fr/HumanCalculator/HumanCalculatorsList"));

// ============================================================================
// HEALTH CALCULATORS - French (31 components)
// ============================================================================

const HealthAddictionCalculator = lazy(() => import("../Components/Fr/HealthCalculator/AddictionCalculator"));
const HealthBAICalculator = lazy(() => import("../Components/Fr/HealthCalculator/BAICalculator"));
const HealthBloodSugarCalculator = lazy(() => import("../Components/Fr/HealthCalculator/BloodSugarCalculator"));
const HealthBMICalculator = lazy(() => import("../Components/Fr/HealthCalculator/BMICalculator"));
const HealthBMIChildCalculator = lazy(() => import("../Components/Fr/HealthCalculator/BMIChildCalculator"));
const HealthBMRCalculator = lazy(() => import("../Components/Fr/HealthCalculator/BMRCalculator"));
const HealthBodyFatCalculator = lazy(() => import("../Components/Fr/HealthCalculator/BodyFatCalculator"));
const HealthBodyTypeCalculator = lazy(() => import("../Components/Fr/HealthCalculator/BodyTypeCalculator"));
const HealthCaloriesCalculator = lazy(() => import("../Components/Fr/HealthCalculator/CaloriesCalculator"));
const HealthCarbohydrateCalculator = lazy(() => import("../Components/Fr/HealthCalculator/CarbohydrateCalculator"));
const HealthCholesterolRatioCalculator = lazy(() => import("../Components/Fr/HealthCalculator/CholesterolRatioCalculator"));
const HealthCoffeeCaffeineCalculator = lazy(() => import("../Components/Fr/HealthCalculator/coffee-caffeine"));
const HealthDiabetesRiskCalculator = lazy(() => import("../Components/Fr/HealthCalculator/DiabetesRiskCalculator"));
const HealthEyeTest = lazy(() => import("../Components/Fr/HealthCalculator/EyeTest"));
const HealthFaceShapeCalculator = lazy(() => import("../Components/Fr/HealthCalculator/FaceShapeCalculator"));
const HealthFoodEnergyCalculator = lazy(() => import("../Components/Fr/HealthCalculator/FoodEnergyCalculator"));
const HealthFoodNutrientsCalculator = lazy(() => import("../Components/Fr/HealthCalculator/FoodNutrientsCalculator"));
const HealthGFRCalculator = lazy(() => import("../Components/Fr/HealthCalculator/GFRCalculator"));
const HealthGlucoseCalculator = lazy(() => import("../Components/Fr/HealthCalculator/GlucoseCalculator"));
const HealthHeartbeatCalculator = lazy(() => import("../Components/Fr/HealthCalculator/HeartbeatCalculator"));
const HealthIdealWeightCalculator = lazy(() => import("../Components/Fr/HealthCalculator/IdealWeightCalculator"));
const HealthMedicineDosageCalculator = lazy(() => import("../Components/Fr/HealthCalculator/MedicineDosageCalculator"));
const HealthParacetamolChildrenDosageCalculator = lazy(() => import("../Components/Fr/HealthCalculator/ParacetamolChildrenDosageCalculator"));
const HealthProteinCalculator = lazy(() => import("../Components/Fr/HealthCalculator/ProteinCalculator"));
const HealthSleapCalculator = lazy(() => import("../Components/Fr/HealthCalculator/SleapCalculator"));
const HealthSmokingRecoveryCalculator = lazy(() => import("../Components/Fr/HealthCalculator/SmokingRecoveryCalculator"));
const HealthStepsToCaloriesCalculator = lazy(() => import("../Components/Fr/HealthCalculator/StepsToCaloriesCalculator"));
const HealthWalkingCalorieCalculator = lazy(() => import("../Components/Fr/HealthCalculator/WalkingCalorieCalculator"));
const HealthWaterIntakeCalculator = lazy(() => import("../Components/Fr/HealthCalculator/WaterIntakeCalculator"));
const HealthDailyVitaminDosageList = lazy(() => import("../Components/Fr/HealthCalculator/DailyVitaminDosage/DosageList"));
const HealthDailyVitaminDosageCalculator = lazy(() => import("../Components/Fr/HealthCalculator/DailyVitaminDosage/DosageCalculator"));

// ============================================================================
// PHYSICS CALCULATORS - French (22+ components)
// ============================================================================

const PhysicsAcceleration = lazy(() => import("../Components/Fr/PhysicsCalculator/acceleration"));
const PhysicsArrivalTime = lazy(() => import("../Components/Fr/PhysicsCalculator/ArrivalTime"));
const PhysicsCubicMeterCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/CubicMeterCalculator"));
const PhysicsElectricalEnergyCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/ElectricalEnergyCalculator"));
const PhysicsElectricCharge = lazy(() => import("../Components/Fr/PhysicsCalculator/electric-charge"));
const PhysicsEngineHorsepower = lazy(() => import("../Components/Fr/PhysicsCalculator/EngineHorsepower"));
const PhysicsFrictionForce = lazy(() => import("../Components/Fr/PhysicsCalculator/FrictionForce"));
const PhysicsKineticEnergyCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/KineticEnergyCalculator"));
const PhysicsLensPowerCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/LensPowerCalculator"));
const PhysicsOhmsLaw = lazy(() => import("../Components/Fr/PhysicsCalculator/OhmsLaw"));
const PhysicsPhotonEnergy = lazy(() => import("../Components/Fr/PhysicsCalculator/PhotonEnergy"));
const PhysicsPotentialEnergyCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/PotentialEnergyCalculator"));
const PhysicsPressure = lazy(() => import("../Components/Fr/PhysicsCalculator/Pressure"));
const PhysicsResistorCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/ResistorCalculator"));
const PhysicsSolarCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/SolarCalculator"));
const PhysicsTransformerCapacityCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/TransformerCapacityCalculator"));
const PhysicsVelocity = lazy(() => import("../Components/Fr/PhysicsCalculator/velocity"));
const PhysicsWaterDensityCalculator = lazy(() => import("../Components/Fr/PhysicsCalculator/WaterDensityCalculator"));
const PhysicsWave = lazy(() => import("../Components/Fr/PhysicsCalculator/Wave"));

// ============================================================================
// CHEMISTRY CALCULATORS - French (12+ components)
// ============================================================================

const ChemistryDensityCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/DensityCalculator"));
const ChemistryDewPointCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/DewPointCalculator"));
const ChemistryDilutionCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/DilutionCalculator"));
const ChemistryGasVolumeCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/GasVolumeCalculator"));
const ChemistryGibbsFreeEnergy = lazy(() => import("../Components/Fr/ChemistryCalculator/gibbs-free-energy"));
const ChemistryIdealGasLawCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/IdealGasLawCalculator"));
const ChemistryIonChargeCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/IonChargeCalculator"));
const ChemistryIronMass = lazy(() => import("../Components/Fr/ChemistryCalculator/iron-mass"));
const ChemistryKspCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/ksp-calculator"));
const ChemistryMolarityCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/MolarityCalculator"));
const ChemistryMolarMassCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/MolarMassCalculator"));
const ChemistryMolecularWeightCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/MolecularWeightCalculator"));
const ChemistryMolesCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/MolesCalculator"));
const ChemistryMetalWeight = lazy(() => import("../Components/Fr/ChemistryCalculator/metal-weight"));
const ChemistryPhCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/PhCalculator"));
const ChemistrySolubilityCalculator = lazy(() => import("../Components/Fr/ChemistryCalculator/SolubilityCalculator"));
const ChemistryTitration = lazy(() => import("../Components/Fr/ChemistryCalculator/titration"));
const ChemistryWaterMass = lazy(() => import("../Components/Fr/ChemistryCalculator/WaterMass"));

// ============================================================================
// MATH CALCULATORS - French (60+ components)
// ============================================================================

const Math5percent = lazy(() => import("../Components/Fr/MathCalculator/5percent"));
const Math10percent = lazy(() => import("../Components/Fr/MathCalculator/10percent"));
const Math15percent = lazy(() => import("../Components/Fr/MathCalculator/15percent"));
const Math20percent = lazy(() => import("../Components/Fr/MathCalculator/20percent"));
const Math25percent = lazy(() => import("../Components/Fr/MathCalculator/25percent"));
const Math30percent = lazy(() => import("../Components/Fr/MathCalculator/30percent"));
const Math35percent = lazy(() => import("../Components/Fr/MathCalculator/35percent"));
const Math40percent = lazy(() => import("../Components/Fr/MathCalculator/40percent"));
const Math45percent = lazy(() => import("../Components/Fr/MathCalculator/45percent"));
const Math75percent = lazy(() => import("../Components/Fr/MathCalculator/75percent"));
const MathAbsencePercentage = lazy(() => import("../Components/Fr/MathCalculator/AbsencePercentage"));
const MathAttendancePercentage = lazy(() => import("../Components/Fr/MathCalculator/AttendancePercentage"));
const MathCircleCalculator = lazy(() => import("../Components/Fr/MathCalculator/CircleCalculator"));
const MathCircleRadius = lazy(() => import("../Components/Fr/MathCalculator/circle-radius"));
const MathConcreteCalculator = lazy(() => import("../Components/Fr/MathCalculator/ConcreteCalculator"));
const MathCylinderCircumference = lazy(() => import("../Components/Fr/MathCalculator/cylinder-circumference"));
const MathFactorCalculator = lazy(() => import("../Components/Fr/MathCalculator/FactorCalculator"));
const MathFractionsCalculator = lazy(() => import("../Components/Fr/MathCalculator/FractionsCalculator"));
const MathGCFCalculator = lazy(() => import("../Components/Fr/MathCalculator/GCFCalculator"));
const MathLCMCalculator = lazy(() => import("../Components/Fr/MathCalculator/LCMCalculator"));
const MathLiterConverter = lazy(() => import("../Components/Fr/MathCalculator/LiterConverter"));
const MathLongDivisionCalculator = lazy(() => import("../Components/Fr/MathCalculator/LongDivisionCalculator"));
const MathMatrixOperations = lazy(() => import("../Components/Fr/MathCalculator/MatrixOperations"));
const MathMedianCalculator = lazy(() => import("../Components/Fr/MathCalculator/MedianCalculator"));
const MathOneFifth = lazy(() => import("../Components/Fr/MathCalculator/OneFifth"));
const MathOneThirdCalculator = lazy(() => import("../Components/Fr/MathCalculator/OneThirdCalculator"));
const MathPercentage = lazy(() => import("../Components/Fr/MathCalculator/Percentage"));
const MathPopulationDensity = lazy(() => import("../Components/Fr/MathCalculator/PopulationDensity"));
const MathProbabilityCalculator = lazy(() => import("../Components/Fr/MathCalculator/ProbabilityCalculator"));
const MathQuarterCalculator = lazy(() => import("../Components/Fr/MathCalculator/QuarterCalculator"));
const MathRemainderCalculator = lazy(() => import("../Components/Fr/MathCalculator/RemainderCalculator"));
const MathRootCalculator = lazy(() => import("../Components/Fr/MathCalculator/RootCalculator"));
const MathRoundingCalculator = lazy(() => import("../Components/Fr/MathCalculator/RoundingCalculator"));
const MathSampleSizeCalculator = lazy(() => import("../Components/Fr/MathCalculator/SampleSizeCalculator"));
const MathSimplifyingFractionsCalculator = lazy(() => import("../Components/Fr/MathCalculator/SimplifyingFractionsCalculator"));
const MathSphereVolume = lazy(() => import("../Components/Fr/MathCalculator/spherevolume"));
const MathSquareDiameterCalculator = lazy(() => import("../Components/Fr/MathCalculator/SquareDiameterCalculator"));
const MathSquaredCalculator = lazy(() => import("../Components/Fr/MathCalculator/SquaredCalculator"));
const MathSquareSideLength = lazy(() => import("../Components/Fr/MathCalculator/square-side-length"));
const MathSuccessFailureCalculator = lazy(() => import("../Components/Fr/MathCalculator/SuccessFailureCalculator"));
const MathTankArea = lazy(() => import("../Components/Fr/MathCalculator/TankArea"));
const MathTrapezoidHeightCalculator = lazy(() => import("../Components/Fr/MathCalculator/TrapezoidHeightCalculator"));
const MathTriangleCalculator = lazy(() => import("../Components/Fr/MathCalculator/TriangleCalculator"));
const MathTriangleAnglesCalculator = lazy(() => import("../Components/Fr/MathCalculator/TriangleAnglesCalculator"));
const MathTriangleHypotenuseCalculator = lazy(() => import("../Components/Fr/MathCalculator/TriangleHypotenuseCalculator"));
const MathTrigonometryCalculator = lazy(() => import("../Components/Fr/MathCalculator/TrigonometryCalculator"));
const MathTwoThirdsCalculator = lazy(() => import("../Components/Fr/MathCalculator/TwoThirdsCalculator"));
const MathVolumesCalculator = lazy(() => import("../Components/Fr/MathCalculator/VolumesCalculator"));
const MathAreaCalculators = lazy(() => import("../Components/Fr/MathCalculator/AreaCalculators"));

// ============================================================================
// WOMEN CALCULATORS - French (8 components)
// ============================================================================

const WomenBraSizeCalculator = lazy(() => import("../Components/Fr/WomenCalculator/BraSizeCalculator"));
const WomenConceptionCalculator = lazy(() => import("../Components/Fr/WomenCalculator/ConceptionCalculator"));
const WomenDueDateCalculator = lazy(() => import("../Components/Fr/WomenCalculator/DueDateCalculator"));
const WomenOvulationCalculator = lazy(() => import("../Components/Fr/WomenCalculator/OvulationCalculator"));
const WomenPeriodCalculator = lazy(() => import("../Components/Fr/WomenCalculator/PeriodCalculator"));
const WomenPregnancyCalculator = lazy(() => import("../Components/Fr/WomenCalculator/PregnancyCalculator"));

// ============================================================================
// FINANCIAL CALCULATORS - French (16+ components)
// ============================================================================

const FinancialCommissionCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/CommissionCalculator"));
const FinancialDailySalaryCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/DailySalaryCalculator"));
const FinancialDiscountCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/DiscountCalculator"));
const FinancialFuelCostCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/FuelCostCalculator"));
const FinancialGoogleAdsenseCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/GoogleAdsenseCalculator"));
const FinancialHourlySalaryCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/HourlySalaryCalculator"));
const FinancialInflationCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/InflationCalculator"));
const FinancialNetSalesCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/NetSalesCalculator"));
const FinancialPaypalFee = lazy(() => import("../Components/Fr/FinancialCalculator/PaypalFeeCalculator"));
const FinancialPeRatio = lazy(() => import("../Components/Fr/FinancialCalculator/PeRatioCalculator"));
const FinancialProfitMarginCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/ProfitMarginCalculator"));
const FinancialRoiCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/RoiCalculator"));
const FinancialSalaryBonusCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/SalaryBonusCalculator"));
const FinancialVATCalculator = lazy(() => import("../Components/Fr/FinancialCalculator/VATCalculator"));

// ============================================================================
// STUDY CALCULATORS - French (26 components)
// ============================================================================

const StudyGPA4 = lazy(() => import("../Components/Fr/StudyCalculator/GPA4"));
const StudyGPA4to5 = lazy(() => import("../Components/Fr/StudyCalculator/GPA4to5"));
const StudyGPA5 = lazy(() => import("../Components/Fr/StudyCalculator/GPA5"));
const StudyGPAPlanner = lazy(() => import("../Components/Fr/StudyCalculator/GPAPlanner"));
const StudyGPATabelConverter = lazy(() => import("../Components/Fr/StudyCalculator/GPATabelConverter"));
const StudyGrade100 = lazy(() => import("../Components/Fr/StudyCalculator/Grade100"));
const StudyGrade100to4 = lazy(() => import("../Components/Fr/StudyCalculator/Grade100to4"));
const StudyGrade100to5 = lazy(() => import("../Components/Fr/StudyCalculator/Grade100to5"));
const StudyGradeGPACalculator = lazy(() => import("../Components/Fr/StudyCalculator/GradeGPACalculator"));
const StudyGradeToCanada = lazy(() => import("../Components/Fr/StudyCalculator/GradeToCanada"));
const StudyGradeToFrench = lazy(() => import("../Components/Fr/StudyCalculator/GradeToFrench"));
const StudyGradeToGerman = lazy(() => import("../Components/Fr/StudyCalculator/GradeToGerman"));
const StudyGradeToUk = lazy(() => import("../Components/Fr/StudyCalculator/GradeToUk"));
const StudyTestGrade = lazy(() => import("../Components/Fr/StudyCalculator/TestGrade"));
const StudyWeightedPercentageCalculator = lazy(() => import("../Components/Fr/StudyCalculator/WeightedPercentageCalculator"));

// ============================================================================
// FOOD CALCULATORS - French (9 components)
// ============================================================================

const FoodCakePanConversion = lazy(() => import("../Components/Fr/FoodCalculator/cake-pan-conversion"));
const FoodChocolateCalculator = lazy(() => import("../Components/Fr/FoodCalculator/chocolate-calculator"));
const FoodCookingMeasurement = lazy(() => import("../Components/Fr/FoodCalculator/cooking-measurement"));
const FoodDonut = lazy(() => import("../Components/Fr/FoodCalculator/donut"));
const FoodFoodCalculator = lazy(() => import("../Components/Fr/FoodCalculator/FoodCalculator"));
const FoodGramsToCups = lazy(() => import("../Components/Fr/FoodCalculator/grams-to-cups"));
const FoodIceCream = lazy(() => import("../Components/Fr/FoodCalculator/ice-cream"));
const FoodPerfectPancake = lazy(() => import("../Components/Fr/FoodCalculator/perfect-pancake"));
const FoodPizza = lazy(() => import("../Components/Fr/FoodCalculator/pizza"));

// ============================================================================
// OTHER CALCULATORS - French (22+ components)
// ============================================================================

const OtherAbsenceCalculator = lazy(() => import("../Components/Fr/OtherCalculator/AbsenceCalculator"));
const OtherBatteryLifeCalculator = lazy(() => import("../Components/Fr/OtherCalculator/BatteryLifeCalculator"));
const OtherEnglishLearningTimeCalculator = lazy(() => import("../Components/Fr/OtherCalculator/EnglishLearningTimeCalculator"));
const OtherFrenchLearningTimeCalculator = lazy(() => import("../Components/Fr/OtherCalculator/FrenchLearningTimeCalculator"));
const OtherGermanLearningTimeCalculator = lazy(() => import("../Components/Fr/OtherCalculator/GermanLearningTimeCalculator"));
const OtherHeightDifferenceCalculator = lazy(() => import("../Components/Fr/OtherCalculator/HeightDifferenceCalculator"));
const OtherInternetSpeed = lazy(() => import("../Components/Fr/OtherCalculator/InternetSpeed"));
const OtherNameGenerator = lazy(() => import("../Components/Fr/OtherCalculator/NameGenerator"));
const OtherOvertimeCalculator = lazy(() => import("../Components/Fr/OtherCalculator/OvertimeCalculator"));
const OtherPasswordGenerator = lazy(() => import("../Components/Fr/OtherCalculator/PasswordGenerator"));
const OtherRandomNumberGenerator = lazy(() => import("../Components/Fr/OtherCalculator/RandomNumberGenerator"));
const OtherTallyCounter = lazy(() => import("../Components/Fr/OtherCalculator/TallyCounter"));
const OtherTextDecorator = lazy(() => import("../Components/Fr/OtherCalculator/TextDecorator"));
const OtherWordCounter = lazy(() => import("../Components/Fr/OtherCalculator/WordCounter"));
const OtherCurrencySpreading = lazy(() => import("../Components/Fr/OtherCalculator/CurrencySpreading"));
const PhysicsEarthMass = lazy(() => import("../Components/Fr/PhysicsCalculator/EarthMassCalculator"));

// ============================================================================
// HUMAN/DATE CALCULATORS - French (14+ components)
// ============================================================================

const HumanAgeCalculator = lazy(() => import("../Components/Fr/HumanCalculator/AgeCalculator"));
const HumanAgeCalculatorGregorian = lazy(() => import("../Components/Fr/HumanCalculator/AgeCalculatorGregorian"));
const HumanAgeCalculatorHijri = lazy(() => import("../Components/Fr/HumanCalculator/AgeCalculatorHijri"));
const HumanAgeDifferencesGregorian = lazy(() => import("../Components/Fr/HumanCalculator/AgeDifferencesGregorian"));
const HumanAnniversaryCalculator = lazy(() => import("../Components/Fr/HumanCalculator/AnniversaryCalculator"));
const HumanBirthDay = lazy(() => import("../Components/Fr/HumanCalculator/BirthDay"));
const HumanConversionToGregorian = lazy(() => import("../Components/Fr/HumanCalculator/ConversionToGregorian"));
const HumanConversionToHijri = lazy(() => import("../Components/Fr/HumanCalculator/ConversionToHijri"));
const HumanCurrentDateDisplay = lazy(() => import("../Components/Fr/HumanCalculator/CurrentDateDisplay"));
const HumanDateDifferencesGregorian = lazy(() => import("../Components/Fr/HumanCalculator/DateDifferencesGregorian"));
const HumanDateDifferencesHijri = lazy(() => import("../Components/Fr/HumanCalculator/DateDifferencesHijri"));
const HumanDaysCalculator = lazy(() => import("../Components/Fr/HumanCalculator/DaysCalculator"));
const HumanDeathCalculator = lazy(() => import("../Components/Fr/HumanCalculator/DeathCalculator"));
const HumanTimeCalculator = lazy(() => import("../Components/Fr/HumanCalculator/TimeCalculator"));
const HumanGregorianYear = lazy(() => import("../Components/Fr/HumanCalculator/GregorianYear/GregorianYear"));
const HumanGregorianYearList = lazy(() => import("../Components/Fr/HumanCalculator/GregorianYear/GregorianYearList"));

// ============================================================================
// ROUTE DEFINITIONS
// ============================================================================

/**
 * CATEGORY ROUTE PAGES
 * These routes point to category list pages that display all calculators in that category
 */
export const frenchCategoryRoutes = [
  { path: "/fr/health-calculators/", component: HealthCalculatorsList },
  { path: "/fr/physics-calculators/", component: PhysicsCalculatorsList },
  { path: "/fr/chemistry-calculators/", component: ChemistryCalculatorsList },
  { path: "/fr/math-calculators/", component: MathCalculatorsList },
  { path: "/fr/women-calculators/", component: WomenCalculatorsList },
  { path: "/fr/financial-calculators/", component: FinancialCalculatorsList },
  { path: "/fr/study-calculators/", component: StudyCalculatorsList },
  { path: "/fr/food-calculators/", component: FoodCalculatorsList },
  { path: "/fr/other-calculators/", component: OtherCalculatorsList },
  { path: "/fr/human-calculators/", component: HumanCalculatorsList },
];

/**
 * HEALTH CALCULATORS - Individual Routes (43 calculators)
 * SEO-friendly paths for each health calculator
 */
export const frenchHealthRoutes = [
  { path: "/calculator/fr/addiction-calculator/", component: HealthAddictionCalculator },
  { path: "/calculator/fr/bai-calculator/", component: HealthBAICalculator },
  { path: "/calculator/fr/blood-sugar-calculator/", component: HealthBloodSugarCalculator },
  { path: "/calculator/fr/bmiadults/", component: HealthBMICalculator },
  { path: "/calculator/fr/bmichildren/", component: HealthBMIChildCalculator },
  { path: "/calculator/fr/body-type-calculator/", component: HealthBodyTypeCalculator },
  { path: "/calculator/fr/cholesterol-ratio-calculator/", component: HealthCholesterolRatioCalculator },
  { path: "/calculator/fr/coffee-caffeine-calculator/", component: HealthCoffeeCaffeineCalculator },
  { path: "/calculator/fr/diabetes-risk-calculator/", component: HealthDiabetesRiskCalculator },
  { path: "/calculator/fr/eye-test/", component: HealthEyeTest },
  { path: "/calculator/fr/face-shape-calculator/", component: HealthFaceShapeCalculator },
  { path: "/calculator/fr/food-nutrients-calculator/", component: HealthFoodNutrientsCalculator },
  { path: "/calculator/fr/gfr-calculator/", component: HealthGFRCalculator },
  { path: "/calculator/fr/glucose-calculator/", component: HealthGlucoseCalculator },
  { path: "/calculator/fr/heartbeat-calculator/", component: HealthHeartbeatCalculator },
  { path: "/calculator/fr/medicine-dosage-calculator/", component: HealthMedicineDosageCalculator },
  { path: "/calculator/fr/paracetamol-children-dosage/", component: HealthParacetamolChildrenDosageCalculator },
  { path: "/calculator/fr/smoking-recovery-calculator/", component: HealthSmokingRecoveryCalculator },
  { path: "/calculator/fr/steps-to-calories-calculator/", component: HealthStepsToCaloriesCalculator },
  { path: "/calculator/fr/walking-calorie-calculator/", component: HealthWalkingCalorieCalculator },
  { path: "/calculator/fr/water-intake-calculator/", component: HealthWaterIntakeCalculator },
  { path: "/calculator/fr/vitamin-daily-dose/", component: HealthDailyVitaminDosageList },
  { path: "/calculator/fr/vitamin-daily-dose/:vitamin/", component: HealthDailyVitaminDosageCalculator },
  // Short-form aliases matching ConstantsFr.js links
  { path: "/calculator/fr/calories/", component: HealthCaloriesCalculator },
  { path: "/calculator/fr/bmr/", component: HealthBMRCalculator },
  { path: "/calculator/fr/idealweight/", component: HealthIdealWeightCalculator },
  { path: "/calculator/fr/bodyfat/", component: HealthBodyFatCalculator },
  { path: "/calculator/fr/carbohydrate/", component: HealthCarbohydrateCalculator },
  { path: "/calculator/fr/foodenergy/", component: HealthFoodEnergyCalculator },
  { path: "/calculator/fr/protein/", component: HealthProteinCalculator },
  { path: "/calculator/fr/sleep/", component: HealthSleapCalculator },
];

/**
 * PHYSICS CALCULATORS - Individual Routes (34 calculators)
 * SEO-friendly paths for each physics calculator
 */
export const frenchPhysicsRoutes = [
  { path: "/calculator/fr/acceleration/", component: PhysicsAcceleration },
  { path: "/calculator/fr/electrical-energy-calculator/", component: PhysicsElectricalEnergyCalculator },
  { path: "/calculator/fr/electric-charge/", component: PhysicsElectricCharge },
  { path: "/calculator/fr/kinetic-energy-calculator/", component: PhysicsKineticEnergyCalculator },
  { path: "/calculator/fr/ohms-law/", component: PhysicsOhmsLaw },
  { path: "/calculator/fr/potential-energy-calculator/", component: PhysicsPotentialEnergyCalculator },
  { path: "/calculator/fr/pressure/", component: PhysicsPressure },
  { path: "/calculator/fr/resistor-calculator/", component: PhysicsResistorCalculator },
  { path: "/calculator/fr/solar-calculator/", component: PhysicsSolarCalculator },
  { path: "/calculator/fr/earth-mass-calculator/", component: PhysicsEarthMass },
  { path: "/calculator/fr/transformer-capacity-calculator/", component: PhysicsTransformerCapacityCalculator },
  { path: "/calculator/fr/velocity/", component: PhysicsVelocity },
  { path: "/calculator/fr/water-density-calculator/", component: PhysicsWaterDensityCalculator },
  { path: "/calculator/fr/wave/", component: PhysicsWave },
  { path: "/calculator/fr/arrivaltime/", component: PhysicsArrivalTime },
  { path: "/calculator/fr/cubicmeter/", component: PhysicsCubicMeterCalculator },
  { path: "/calculator/fr/lenspower/", component: PhysicsLensPowerCalculator },
  { path: "/calculator/fr/photonenergy/", component: PhysicsPhotonEnergy },
  { path: "/calculator/fr/frictionforce/", component: PhysicsFrictionForce },
  { path: "/calculator/fr/horsepower/", component: PhysicsEngineHorsepower },
];

/**
 * CHEMISTRY CALCULATORS - Individual Routes (20 calculators)
 * SEO-friendly paths for each chemistry calculator
 */
export const frenchChemistryRoutes = [
  { path: "/calculator/fr/density-calculator/", component: ChemistryDensityCalculator },
  { path: "/calculator/fr/dew-point-calculator/", component: ChemistryDewPointCalculator },
  { path: "/calculator/fr/dilution-calculator/", component: ChemistryDilutionCalculator },
  { path: "/calculator/fr/gas-volume-calculator/", component: ChemistryGasVolumeCalculator },
  { path: "/calculator/fr/gibbs-free-energy/", component: ChemistryGibbsFreeEnergy },
  { path: "/calculator/fr/ideal-gas-law-calculator/", component: ChemistryIdealGasLawCalculator },
  { path: "/calculator/fr/ion-charge-calculator/", component: ChemistryIonChargeCalculator },
  { path: "/calculator/fr/iron-mass/", component: ChemistryIronMass },
  { path: "/calculator/fr/ksp-calculator/", component: ChemistryKspCalculator },
  { path: "/calculator/fr/molarity-calculator/", component: ChemistryMolarityCalculator },
  { path: "/calculator/fr/molar-mass-calculator/", component: ChemistryMolarMassCalculator },
  { path: "/calculator/fr/molecular-weight-calculator/", component: ChemistryMolecularWeightCalculator },
  { path: "/calculator/fr/moles-calculator/", component: ChemistryMolesCalculator },
  { path: "/calculator/fr/metal-weight/", component: ChemistryMetalWeight },
  { path: "/calculator/fr/ph-calculator/", component: ChemistryPhCalculator },
  { path: "/calculator/fr/solubility-calculator/", component: ChemistrySolubilityCalculator },
  { path: "/calculator/fr/titration/", component: ChemistryTitration },
  { path: "/calculator/fr/water-mass/", component: ChemistryWaterMass },
];

/**
 * MATH CALCULATORS - Individual Routes (62 calculators)
 * SEO-friendly paths for each math calculator
 */
export const frenchMathRoutes = [
  { path: "/calculator/fr/absence-percentage/", component: MathAbsencePercentage },
  { path: "/calculator/fr/attendance-percentage/", component: MathAttendancePercentage },
  { path: "/calculator/fr/circle-calculator/", component: MathCircleCalculator },
  { path: "/calculator/fr/circle-radius/", component: MathCircleRadius },
  { path: "/calculator/fr/concrete-calculator/", component: MathConcreteCalculator },
  { path: "/calculator/fr/cylinder-circumference/", component: MathCylinderCircumference },
  { path: "/calculator/fr/factor-calculator/", component: MathFactorCalculator },
  { path: "/calculator/fr/fractions-calculator/", component: MathFractionsCalculator },
  { path: "/calculator/fr/gcf-calculator/", component: MathGCFCalculator },
  { path: "/calculator/fr/lcm-calculator/", component: MathLCMCalculator },
  { path: "/calculator/fr/liter-converter/", component: MathLiterConverter },
  { path: "/calculator/fr/long-division-calculator/", component: MathLongDivisionCalculator },
  { path: "/calculator/fr/matrix-operations/", component: MathMatrixOperations },
  { path: "/calculator/fr/one-fifth/", component: MathOneFifth },
  { path: "/calculator/fr/one-third-calculator/", component: MathOneThirdCalculator },
  { path: "/calculator/fr/percentage/", component: MathPercentage },
  { path: "/calculator/fr/population-density/", component: MathPopulationDensity },
  { path: "/calculator/fr/probability-calculator/", component: MathProbabilityCalculator },
  { path: "/calculator/fr/quarter-calculator/", component: MathQuarterCalculator },
  { path: "/calculator/fr/remainder-calculator/", component: MathRemainderCalculator },
  { path: "/calculator/fr/rounding-calculator/", component: MathRoundingCalculator },
  { path: "/calculator/fr/sample-size-calculator/", component: MathSampleSizeCalculator },
  { path: "/calculator/fr/simplifying-fractions-calculator/", component: MathSimplifyingFractionsCalculator },
  { path: "/calculator/fr/sphere-volume/", component: MathSphereVolume },
  { path: "/calculator/fr/square-diameter-calculator/", component: MathSquareDiameterCalculator },
  { path: "/calculator/fr/squared-calculator/", component: MathSquaredCalculator },
  { path: "/calculator/fr/square-side-length/", component: MathSquareSideLength },
  { path: "/calculator/fr/tank-area/", component: MathTankArea },
  { path: "/calculator/fr/trapezoid-height-calculator/", component: MathTrapezoidHeightCalculator },
  { path: "/calculator/fr/triangle-calculator/", component: MathTriangleCalculator },
  { path: "/calculator/fr/triangle-angles-calculator/", component: MathTriangleAnglesCalculator },
  { path: "/calculator/fr/triangle-hypotenuse-calculator/", component: MathTriangleHypotenuseCalculator },
  { path: "/calculator/fr/trigonometry-calculator/", component: MathTrigonometryCalculator },
  { path: "/calculator/fr/two-thirds-calculator/", component: MathTwoThirdsCalculator },
  { path: "/calculator/fr/volumes/", component: MathVolumesCalculator },
  { path: "/calculator/fr/areas/", component: MathAreaCalculators },
  { path: "/calculator/fr/roots/", component: MathRootCalculator },
  { path: "/calculator/fr/median/", component: MathMedianCalculator },
  { path: "/calculator/fr/success-failure-rate/", component: MathSuccessFailureCalculator },
  { path: "/calculator/fr/5percent/", component: Math5percent },
  { path: "/calculator/fr/10percent/", component: Math10percent },
  { path: "/calculator/fr/15percent/", component: Math15percent },
  { path: "/calculator/fr/20percent/", component: Math20percent },
  { path: "/calculator/fr/25percent/", component: Math25percent },
  { path: "/calculator/fr/30percent/", component: Math30percent },
  { path: "/calculator/fr/35percent/", component: Math35percent },
  { path: "/calculator/fr/40percent/", component: Math40percent },
  { path: "/calculator/fr/45percent/", component: Math45percent },
  { path: "/calculator/fr/75percent/", component: Math75percent },
];

/**
 * WOMEN CALCULATORS - Individual Routes (15 calculators)
 * SEO-friendly paths for each women's health calculator
 */
export const frenchWomenRoutes = [
  { path: "/calculator/fr/bra-size-calculator/", component: WomenBraSizeCalculator },
  { path: "/calculator/fr/period-calculator/", component: WomenPeriodCalculator },
  { path: "/calculator/fr/period/", component: WomenPeriodCalculator },
  { path: "/calculator/fr/pregnancy/", component: WomenPregnancyCalculator },
  { path: "/calculator/fr/pregnancy-due-date/", component: WomenDueDateCalculator },
  { path: "/calculator/fr/ovulation/", component: WomenOvulationCalculator },
  { path: "/calculator/fr/conception/", component: WomenConceptionCalculator },
];

/**
 * FINANCIAL CALCULATORS - Individual Routes (20 calculators)
 * SEO-friendly paths for each financial calculator
 */
export const frenchFinancialRoutes = [
  { path: "/calculator/fr/commission-calculator/", component: FinancialCommissionCalculator },
  { path: "/calculator/fr/daily-salary-calculator/", component: FinancialDailySalaryCalculator },
  { path: "/calculator/fr/discount-calculator/", component: FinancialDiscountCalculator },
  { path: "/calculator/fr/fuel-cost-calculator/", component: FinancialFuelCostCalculator },
  { path: "/calculator/fr/hourly-salary-calculator/", component: FinancialHourlySalaryCalculator },
  { path: "/calculator/fr/net-sales-calculator/", component: FinancialNetSalesCalculator },
  { path: "/calculator/fr/paypal-fee/", component: FinancialPaypalFee },
  { path: "/calculator/fr/pe-ratio/", component: FinancialPeRatio },
  { path: "/calculator/fr/profit-margin-calculator/", component: FinancialProfitMarginCalculator },
  { path: "/calculator/fr/roi-calculator/", component: FinancialRoiCalculator },
  { path: "/calculator/fr/salary-bonus-calculator/", component: FinancialSalaryBonusCalculator },
  { path: "/calculator/fr/inflation/", component: FinancialInflationCalculator },
  { path: "/calculator/fr/google-adsense/", component: FinancialGoogleAdsenseCalculator },
  { path: "/calculator/fr/vat/", component: FinancialVATCalculator },
];

/**
 * STUDY CALCULATORS - Individual Routes (26 calculators)
 * SEO-friendly paths for each study/GPA calculator
 */
export const frenchStudyRoutes = [
  { path: "/calculator/fr/test-grade/", component: StudyTestGrade },
  { path: "/calculator/fr/weighted-percentage-calculator/", component: StudyWeightedPercentageCalculator },
  { path: "/calculator/fr/gpatableconverter/", component: StudyGPATabelConverter },
  { path: "/calculator/fr/gpagrade/", component: StudyGradeGPACalculator },
  { path: "/calculator/fr/gpa4/", component: StudyGPA4 },
  { path: "/calculator/fr/gpa5/", component: StudyGPA5 },
  { path: "/calculator/fr/grade100/", component: StudyGrade100 },
  { path: "/calculator/fr/gpaplanner/", component: StudyGPAPlanner },
  { path: "/calculator/fr/grade100to4/", component: StudyGrade100to4 },
  { path: "/calculator/fr/grade100to5/", component: StudyGrade100to5 },
  { path: "/calculator/fr/gpa4to5/", component: StudyGPA4to5 },
  { path: "/calculator/fr/gradetouk/", component: StudyGradeToUk },
  { path: "/calculator/fr/gradetogerman/", component: StudyGradeToGerman },
  { path: "/calculator/fr/gradetofrench/", component: StudyGradeToFrench },
  { path: "/calculator/fr/gradetocanada/", component: StudyGradeToCanada },

];

/**
 * FOOD CALCULATORS - Individual Routes (9 calculators)
 * SEO-friendly paths for each food/cooking calculator
 */
export const frenchFoodRoutes = [
  { path: "/calculator/fr/cake-pan-conversion/", component: FoodCakePanConversion },
  { path: "/calculator/fr/chocolate-calculator/", component: FoodChocolateCalculator },
  { path: "/calculator/fr/cooking-measurement/", component: FoodCookingMeasurement },
  { path: "/calculator/fr/food-calculator/", component: FoodFoodCalculator },
  { path: "/calculator/fr/grams-to-cups/", component: FoodGramsToCups },
  { path: "/calculator/fr/pizza/", component: FoodPizza },
  { path: "/calculator/fr/ice-cream/", component: FoodIceCream },
  { path: "/calculator/fr/perfect-pancake/", component: FoodPerfectPancake },
  { path: "/calculator/fr/donut/", component: FoodDonut },
];

/**
 * OTHER CALCULATORS - Individual Routes (23 calculators)
 * SEO-friendly paths for miscellaneous calculators
 */
export const frenchOtherRoutes = [
  { path: "/calculator/fr/absence-calculator/", component: OtherAbsenceCalculator },
  { path: "/calculator/fr/battery-life-calculator/", component: OtherBatteryLifeCalculator },
  { path: "/calculator/fr/english-learning-time-calculator/", component: OtherEnglishLearningTimeCalculator },
  { path: "/calculator/fr/french-learning-time-calculator/", component: OtherFrenchLearningTimeCalculator },
  { path: "/calculator/fr/german-learning-time-calculator/", component: OtherGermanLearningTimeCalculator },
  { path: "/calculator/fr/height-difference-calculator/", component: OtherHeightDifferenceCalculator },
  { path: "/calculator/fr/internet-speed/", component: OtherInternetSpeed },
  { path: "/calculator/fr/overtime-calculator/", component: OtherOvertimeCalculator },
  { path: "/calculator/fr/random-number-generator/", component: OtherRandomNumberGenerator },
  { path: "/calculator/fr/tally-counter/", component: OtherTallyCounter },
  { path: "/calculator/fr/text-decorator/", component: OtherTextDecorator },
  { path: "/calculator/fr/random-password-generator/", component: OtherPasswordGenerator },
  { path: "/calculator/fr/random-name-generator/", component: OtherNameGenerator },
  { path: "/calculator/fr/word-counter/", component: OtherWordCounter },
  { path: "/calculator/fr/currency-spreading/", component: OtherCurrencySpreading },
];

/**
 * HUMAN/DATE CALCULATORS - Individual Routes (14 calculators)
 * SEO-friendly paths for human/date calculators
 */
export const frenchHumanRoutes = [
  { path: "/calculator/fr/age-calculator/", component: HumanAgeCalculator },
  { path: "/calculator/fr/anniversary-calculator/", component: HumanAnniversaryCalculator },
  { path: "/calculator/fr/birthday/", component: HumanBirthDay },
  { path: "/calculator/fr/current-date-display/", component: HumanCurrentDateDisplay },
  { path: "/calculator/fr/days-calculator/", component: HumanDaysCalculator },
  { path: "/calculator/fr/death-calculator/", component: HumanDeathCalculator },
  { path: "/calculator/fr/time-calculator/", component: HumanTimeCalculator },
  // Short-form aliases matching ConstantsFr.js links
  { path: "/calculator/fr/agegregorian/", component: HumanAgeCalculatorGregorian },
  { path: "/calculator/fr/agehijri/", component: HumanAgeCalculatorHijri },
  { path: "/calculator/fr/gregoriandifferences/", component: HumanAgeDifferencesGregorian },
  { path: "/calculator/fr/dategregoriandifferences/", component: HumanDateDifferencesGregorian },
  { path: "/calculator/fr/datehijridifferences/", component: HumanDateDifferencesHijri },
  { path: "/calculator/fr/gregorian_to_hijri/", component: HumanConversionToHijri },
  { path: "/calculator/fr/hijri_to_gregorian/", component: HumanConversionToGregorian },
  { path: "/calculator/fr/gregorian/:year/", component: HumanGregorianYear },
  { path: "/calculator/fr/gregorian/gregorian-year-list/", component: HumanGregorianYearList },
];

// ============================================================================
// ISLAMIC CALCULATORS - French
// ============================================================================
const IslamicMoneyZakat = lazy(() => import("../Components/Fr/IslamicCalculator/MoneyZakatCalculator"));
const IslamicGoldZakat = lazy(() => import("../Components/Fr/IslamicCalculator/GoldZakatCalculator"));
const IslamicSilverZakat = lazy(() => import("../Components/Fr/IslamicCalculator/SilverZakatCalculator"));
const IslamicFruitsZakat = lazy(() => import("../Components/Fr/IslamicCalculator/FruitsZakatCalculator"));
const IslamicTradeZakat = lazy(() => import("../Components/Fr/IslamicCalculator/TradeZakatCalculator"));
const IslamicAnimalZakat = lazy(() => import("../Components/Fr/IslamicCalculator/AnimalZakatCalculator"));
const IslamicGoldKarat = lazy(() => import("../Components/Fr/IslamicCalculator/GoldKaratCalculator"));
const IslamicCalculatorList = lazy(() => import("../Components/Fr/IslamicCalculator/IslamicCalculatorList"));
const IslamicAthkarAlMasaa = lazy(() => import("../Components/Fr/IslamicCalculator/athkar-almasaa"));
const IslamicAthkarAlSabah = lazy(() => import("../Components/Fr/IslamicCalculator/athkar-alsabah"));
const IslamicTahajjudCalculator = lazy(() => import("../Components/Fr/IslamicCalculator/TahajjudTimeCalculator"));

export const frenchIslamicRoutes = [
  { path: "/calculator/fr/money-zakat/", component: IslamicMoneyZakat },
  { path: "/calculator/fr/gold-zakat/", component: IslamicGoldZakat },
  { path: "/calculator/fr/silver-zakat/", component: IslamicSilverZakat },
  { path: "/calculator/fr/fruits-zakat/", component: IslamicFruitsZakat },
  { path: "/calculator/fr/trade-zakat/", component: IslamicTradeZakat },
  { path: "/calculator/fr/animals-zakat/", component: IslamicAnimalZakat },
  { path: "/calculator/fr/gold-karat/", component: IslamicGoldKarat },
  { path: "/calculator/fr/islamic-calculators/", component: IslamicCalculatorList },
  { path: "/calculator/fr/athkar-almasaa/", component: IslamicAthkarAlMasaa },
  { path: "/calculator/fr/athkar-alsabah/", component: IslamicAthkarAlSabah },
  { path: "/calculator/fr/tahajjud-time/", component: IslamicTahajjudCalculator },
];

/**
 * COMPLETE FRENCH ROUTES ARRAY
 * Combines all French calculator routes for easy access and registration
 * Total: 10 category pages + 276 individual calculator routes = 286 total routes
 */
export const allFrenchRoutes = [
  ...frenchCategoryRoutes,
  ...frenchHealthRoutes,
  ...frenchPhysicsRoutes,
  ...frenchChemistryRoutes,
  ...frenchMathRoutes,
  ...frenchWomenRoutes,
  ...frenchFinancialRoutes,
  ...frenchStudyRoutes,
  ...frenchFoodRoutes,
  ...frenchOtherRoutes,
  ...frenchHumanRoutes,
  ...frenchIslamicRoutes,
];

export default allFrenchRoutes;
